import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import { useTranslation } from 'react-i18next';
import FillWidthCell from '../../../app/components/FillWidthCell';
import StyledTableRow from '../../../app/components/StyledTableRow';
import { useNavigate, useParams } from 'react-router-dom';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatCurrency } from '../../../app/utils/formatters';
import EmptyScreen from '../../../app/components/EmptyScreen';
import { getOrdersAsync } from '../../manager/orders/orderListSlice';
import { Order } from '../../../app/data/models/orders/order';
import { v4 as uuidv4 } from 'uuid';
import {
  getWaiterOrdersAsync,
  loadRestaurantCartsAsync,
  clearWaiterCart,
  setOrderTab,
} from './waiterOrderSlice';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { getOrderTypeChipProps } from '../../../app/utils/orderUtils';
import { Cart } from '../../../app/data/models/cart';

const WaiterOrderListPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.account);
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { orders, nextPageId, status } = useAppSelector(
    (state) => state.orders
  );
  const {
    carts,
    waiterOrdersStatus,
    orders: waiterOrders,
    waiterOrderNextPageId,
    orderTab,
  } = useAppSelector((state) => state.waiterOrder);
  const [tabValue, setTabValue] = useState(orderTab);
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  useEffect(() => {
    dispatch(setOrderTab(tabValue));
  }, [tabValue, dispatch]);

  useEffect(() => {
    if (restaurantId && user)
      dispatch(getWaiterOrdersAsync({ restaurantId, waiterId: user.id }));
  }, [dispatch, restaurantId, user]);

  const handleTabChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: number
  ) => {
    setTabValue(newValue);
    setPageHistory([]);
    if (restaurantId) {
      if (newValue === 0) {
        if (user) {
          dispatch(getWaiterOrdersAsync({ restaurantId, waiterId: user.id }));
        }
      } else if (newValue === 1) {
        dispatch(loadRestaurantCartsAsync(restaurantId));
      } else if (newValue === 2) {
        dispatch(getOrdersAsync({ restaurantId }));
      }
    }
  };

  const handleRowClick = (orderId: string) => {
    navigate(
      `/restaurant-waiter/restaurants/${restaurantId}/orders/${orderId}`
    );
  };

  const handleNextPage = () => {
    if (!restaurantId) return;
    if (nextPageId && tabValue === 2) {
      dispatch(getOrdersAsync({ restaurantId, nextPageId }));
      setPageHistory((prev) => [...prev, nextPageId || undefined]);
    } else if (waiterOrderNextPageId && tabValue === 0 && user) {
      dispatch(
        getWaiterOrdersAsync({
          restaurantId,
          waiterId: user.id,
          nextPageId: waiterOrderNextPageId,
        })
      );
      setPageHistory((prev) => [...prev, waiterOrderNextPageId || undefined]);
    }
  };

  const handlePreviousPage = () => {
    if (!restaurantId || pageHistory.length === 0) return;

    if (tabValue === 2) {
      const newHistory = [...pageHistory];
      newHistory.pop();
      const previousPageId = newHistory[newHistory.length - 1] || undefined;
      setPageHistory(newHistory);
      dispatch(getOrdersAsync({ restaurantId, nextPageId: previousPageId }));
    } else if (tabValue === 0 && user) {
      const newHistory = [...pageHistory];
      newHistory.pop();
      const previousPageId = newHistory[newHistory.length - 1] || undefined;
      setPageHistory(newHistory);
      dispatch(
        getWaiterOrdersAsync({
          restaurantId,
          waiterId: user.id,
          nextPageId: previousPageId,
        })
      );
    }
  };

  const handleDraftRowClick = (cartId: string | undefined) => {
    if (cartId) {
      navigate(
        `/restaurant-waiter/restaurants/${restaurantId}/orders/create/${cartId}`
      );
    }
  };

  const handleDeleteDraft = (
    cartId: string | undefined,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (restaurantId && cartId) {
      dispatch(clearWaiterCart({ cartId, restaurantId }));
    }
  };

  const renderMyOrders = () => {
    if (waiterOrdersStatus.includes('pending')) return <LoadingComponent />;

    if (!waiterOrders || waiterOrders.length === 0) {
      return <EmptyScreen emptyText={t('orders.list.my-orders-empty')} />;
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: 1,
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Table aria-label={t('orders.list.title')} size="small">
          <TableHead>
            <TableRow>
              <TableCell
                width="50px"
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                #
              </TableCell>
              <FillWidthCell
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.order-number')}
              </FillWidthCell>
              <TableCell
                align="right"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.total')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  display: { xs: 'none', md: 'table-cell' },
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {waiterOrders.map((order: Order, index: number) => (
              <StyledTableRow
                key={order.id}
                onClick={() => handleRowClick(order.id)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                <TableCell
                  width="50px"
                  align="left"
                  component="th"
                  scope="row"
                  sx={{ py: 2 }}
                >
                  {index + 1}
                </TableCell>
                <TableCell align="left" sx={{ py: 2 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'medium',
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      #{order.tokenNumber}
                    </Typography>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <OrderStatusLabel orderStatus={order.orderStatus} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography>{formatCurrency(order.total)}</Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', md: 'table-cell' },
                    verticalAlign: 'top',
                    py: 2,
                  }}
                >
                  <OrderStatusLabel orderStatus={order.orderStatus} />
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAllOrders = () => {
    if (status.includes('pending')) return <LoadingComponent />;

    if (!orders || orders.length === 0) {
      return <EmptyScreen emptyText={t('orders.list.empty')} />;
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: 1,
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Table aria-label={t('orders.list.title')} size="small">
          <TableHead>
            <TableRow>
              <TableCell
                width="50px"
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                #
              </TableCell>
              <FillWidthCell
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.order-number')}
              </FillWidthCell>
              <TableCell
                align="right"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.total')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  display: { xs: 'none', md: 'table-cell' },
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order, index) => (
              <StyledTableRow
                key={order.id}
                onClick={() => handleRowClick(order.id)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                <TableCell
                  width="50px"
                  align="left"
                  component="th"
                  scope="row"
                  sx={{ py: 2 }}
                >
                  {index + 1}
                </TableCell>
                <TableCell align="left" sx={{ py: 2 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'medium',
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      #{order.tokenNumber}
                    </Typography>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <OrderStatusLabel orderStatus={order.orderStatus} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography>{formatCurrency(order.total)}</Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', md: 'table-cell' },
                    verticalAlign: 'top',
                    py: 2,
                  }}
                >
                  <OrderStatusLabel orderStatus={order.orderStatus} />
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDraftOrders = () => {
    if (!carts || carts.length === 0) {
      return <EmptyScreen emptyText={t('orders.list.draft-orders-empty')} />;
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: 1,
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Table aria-label="Draft Orders" size="small">
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.order-info')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.total')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  display: { md: 'table-cell' },
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.items-count')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carts.map((cart: Cart) => {
              const orderType = cart.orderType || OrderType.DineIn;
              const customerInfo =
                orderType === OrderType.DineIn
                  ? t('orders.list.draft.table-number', {
                      number: cart.tableNumber,
                    }) || t('orders.list.draft.unknown-table')
                  : cart.customerName ||
                    cart.customerPhone ||
                    t('orders.list.draft.unknown-customer');

              return (
                <StyledTableRow
                  key={cart.id}
                  onClick={() => handleDraftRowClick(cart.id)}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <TableCell align="left" sx={{ py: 2 }}>
                    <Typography>{customerInfo}</Typography>
                    <Chip
                      size="small"
                      {...getOrderTypeChipProps(orderType)}
                      label={t(getOrderTypeChipProps(orderType).label)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    {formatCurrency(cart.totalPrice)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      display: { md: 'table-cell' },
                      py: 2,
                    }}
                  >
                    {cart.totalQuantity}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    <IconButton
                      onClick={(e) => handleDeleteDraft(cart.id, e)}
                      size="small"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 0 }}>
      <ListTitleBox
        title={t('orders.list.title')}
        onPrimaryClick={() =>
          navigate(
            `/restaurant-waiter/restaurants/${restaurantId}/orders/create/${uuidv4()}`
          )
        }
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={tabValue}
          exclusive
          onChange={handleTabChange}
          aria-label="order tabs"
        >
          <ToggleButton value={0} aria-label="my orders">
            {t('orders.list.my-orders')}
          </ToggleButton>
          <ToggleButton value={1} aria-label="draft orders">
            {t('orders.list.draft-orders')}
          </ToggleButton>
          <ToggleButton value={2} aria-label="all orders">
            {t('orders.list.all-orders')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {tabValue === 0 && renderMyOrders()}
      {tabValue === 1 && renderDraftOrders()}
      {tabValue === 2 && renderAllOrders()}

      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2 }}>
        <IconButton
          onClick={handlePreviousPage}
          disabled={pageHistory.length === 0}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={
            (tabValue === 2 && !nextPageId) ||
            (tabValue === 0 && !waiterOrderNextPageId) ||
            tabValue === 1
          }
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default WaiterOrderListPage;
